<template>
  <div class="checkbox__container">
    <div class="checkbox"
         :style="{'z-index': calculateZIndex}"
         :class="{ 'open': isOpen, 'disabled': disabled}">
      <div v-click-outside="onClickOutside"
           class="checkbox__heading no-select"
           @click="toggleOpen">
        <h4>
          {{ displayLabel }}
          <span v-for="(item, id) in selectedItems"
                :key="id"
                class="checkbox__heading-item">
            {{ item }}<span v-if="id < selectedItems.length - 1"
                            :style="{color: '#7f7f7f'}">,</span>
          </span>
        </h4>
        <span class="collapse-indicator">
          <font-awesome-icon class="icon"
                             icon="chevron-down"
                             role="presentation"></font-awesome-icon>
        </span>
      </div>
      <transition name="checkbox__body"
                  @before-enter="beforeEnterDropdown"
                  @enter="enterDropdown"
                  @before-leave="beforeLeaveDropdown"
                  @leave="leaveDropdown">
        <div v-show="isOpen"
             class="checkbox__body">
          <div class="checkbox__content">
            <TransitionGroup name="checkbox-list"
                             tag="ul"
                             @enter="enter"
                             @leave="leave">
              <!-- eslint-disable vue/no-use-v-if-with-v-for -->
              <li v-for="(option, id) in options"
                  v-if="isOpen"
                  :key="option.key"
                  :data-index="id">
                <input v-uni-id="option.key"
                       :value="option.key"
                       type="checkbox"
                       @change="updateValue(option.key)">
                <label v-uni-for="option.key">
                  <span class="fv-label">
                    {{ option.value }}
                  </span>
                  <font-awesome-icon class="selected-check"
                                     :class="{selected: isSelected(option.key)}"
                                     icon="check"></font-awesome-icon>
                </label>
              </li>
              <!-- eslint-enable vue/no-use-v-if-with-v-for -->

              <li v-if="options.length === 0"
                  key="no"
                  :data-index="0">
                <span class="no-options">
                  No applicable options
                </span>
              </li>
            </TransitionGroup>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { TweenMax } from "gsap";
import ClickOutside from "vue-click-outside";

export default {
  name: "AnimatedDropdown",
  directives: {
    ClickOutside,
  },
  props: {
    label: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      default: () => [],
    },
    selectMultiple: {
      type: Boolean,
      default: true,
    },
    index: {
      type: Number,
      required: true,
    },
    closeOnSelect: {
      type: Boolean,
      default: false,
    },
    showLabelWhenSelected: {
      type: Boolean,
      default: true,
    },
    selected: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      selectedValues: [],
      isOpen: false,
      startZIndex: 500,
    };
  },
  computed: {
    calculateZIndex () {
      return this.startZIndex - this.index;
    },
    displayLabel () {
      let result = null;

      if ( this.showLabelWhenSelected ) {
        result = this.label;

        if ( this.selectedItems.length > 0 ) {
          result += " -";
        }
      } else if ( this.selectedItems.length === 0 ) {
        result = this.label;
      }

      return result;
    },
    selectedItems () {
      const result = [];

      if ( this.selectedValues.length > 0 ) {
        for ( let i = 0; i < this.selectedValues.length; i += 1 ) {
          const option = this.options.find( o => o.key === this.selectedValues[i] );
          if ( option ) {
            result.push( option.value );
          }
        }
      }

      return result;
    },
  },
  watch: {
    selected: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.selectedValues = val;
        }
      },
    },
  },
  mounted () {
    this.popupItem = this.$el;
  },
  methods: {
    toggleOpen () {
      if ( !this.disabled ) {
        this.isOpen = !this.isOpen;
      }
    },
    getFilterOpenClass () {
      return this.isOpen ? "open" : "";
    },
    onClickOutside () {
      this.isOpen = false;
    },
    updateValue ( key ) {
      if ( this.selectedValues.includes( key ) ) {
        this.selectedValues = this.selectedValues.filter( v => v !== key );
      } else {
        if ( !this.selectMultiple ) {
          this.selectedValues = [];
        }
        this.selectedValues.push( key );
      }

      if ( this.closeOnSelect ) {
        this.isOpen = false;
      }

      this.$emit( "changed", this.selectedValues, this.index );
    },
    isSelected ( key ) {
      let result = false;

      if ( this.selectedValues && this.selectedValues.length > 0 ) {
        result = this.selectedValues.includes( key );
      }

      return result;
    },
    enter ( el, done ) {
      const delay = ( el.dataset.index * 0.01 );

      el.style.opacity = 0;
      TweenMax.killTweensOf( el );

      TweenMax.to(
        el,
        0.1,
        { opacity: 1, delay, onComplete: done },
      );
    },
    leave ( el, done ) {
      TweenMax.killTweensOf( el );

      TweenMax.to(
        el,
        0.2,
        { opacity: 0, onComplete: done },
      );
    },
    beforeEnterDropdown ( el ) {
      el.style.height = "auto";
      el.style.overflow = "hidden";
    },
    enterDropdown ( el, done ) {
      TweenMax.from(
        el,
        0.2,
        {
          height: "0",
          onComplete: () => {
            el.style.overflow = "auto";
            done();
          },
        },
      );
    },
    beforeLeaveDropdown ( el ) {
      el.style.overflow = "hidden";
    },
    leaveDropdown ( el, done ) {
      TweenMax.to(
        el,
        0.2,
        {
          height: 0,
          onComplete: () => {
            el.style.overflow = "auto";
            done();
          },
        },
      );
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.checkbox {
  box-shadow: 0 0 5px 0 rgba( 0, 0, 0, 0.2 );
  border-radius: 5px;
  position: relative;
  width: 250px;
  transition: box-shadow, background-color .25s ease-out;

  @media ( max-width: 767px ) {
    width: 100%;
  }

  &__container {
    display: block;
    height: 50px;
    min-width: 200px;
    margin-bottom: 10px;
    margin-right: 20px;

    @media ( max-width: 767px ) {
      width: 100%;
    }
  }

  &__heading {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 0 20px;
    padding: 10px 0;
    position: relative;
    cursor: default;

    &-item {
      color: @color-accent-1;
      transition: color .25s ease-out;
      font-size: 14px;
    }

    h4 {
      color: #7f7f7f;
      font-size: 16px;
      font-weight: 500;
      padding: 5px 10px;
      padding-left: 0;
      margin: 0;
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: color .25s ease-out;
    }

    .collapse-indicator {
      padding-left: 15px;
      display: flex;
      justify-content: flex;
      align-items: center;
      border-left: 1px solid #aaa;

      .icon {
        transition: transform .25s ease-out;
      }
    }

    &::after {
      content: "";
      display: block;
      width: 100%;
      height: 1px;
      background-color: @color-accent-1;
      position: absolute;
      bottom: 0;
      opacity: 0;
      transition: opacity .25s ease-out;
    }
  }

  &.open {
    .checkbox__heading::after {
      opacity: 1;
    }

    .collapse-indicator > .icon {
      transform: rotate( 180deg );
    }
  }

  &__body {
    position: relative;
    background-color: #fff;
    border-radius: 0 0 5px 5px;
    max-width: 250px;
    transition: background-color .25s ease-out;

    @media ( max-width: 767px ) {
      max-width: 100%;
    }
  }

  &.disabled {
    background-color: #eaeaea !important;
    box-shadow: 0 0 5px 0 rgba( 0, 0, 0, 0 );
    color: #aaa;

    .checkbox__body,
    .checkbox__heading-item,
    .checkbox__heading > h4 {
      color: #aaa;
    }

    .checkbox__body {
      background-color: #eaeaea !important;
    }
  }

  &__content {
    width: 100%;
    padding: 0 20px;
    margin: 0 -10px;
    z-index: 100;

    ul {
      margin: 0;
      padding: 0;
      li {
        display: flex;
        align-items: center;
        margin: 0 10px;
        padding: 10px 0;
        cursor: pointer;
        position: relative;
        width: 100%;

        &:not( :last-child ) {
          border-bottom: 1px solid #aaa;
        }

        input {
          display: none;
        }

        label {
          align-items: center;
          display: flex;
          justify-content: space-between;
          width: 100%;
          margin: 0;
          cursor: pointer;

          span {
            cursor: pointer;
          }

          .fv-label {
            display: inline-block;
          }

          .selected-check {
            color: @color-accent-1;
            opacity: 0;
            transition: opacity 0.1s ease-out;

            &.selected {
              opacity: 1;
            }
          }
        }
      }
    }
  }
}
</style>
